import { PhoneNumberFormat, PhoneNumberType, PhoneNumberUtil } from "google-libphonenumber";

const phoneUtil = PhoneNumberUtil.getInstance();
const ALLOWED_CHARS_REGEX = /^[0-9+\-()\s]+$/;

const containsInvalidChars = (input: string): boolean => !ALLOWED_CHARS_REGEX.test(input);

const normalizeInput = (input: string): string =>
    input.trim().replace(/\s+/g, " ").replace(/[()\-]/g, "");

export const normalizeCallingCode = (input?: string | null): string | null => {
    if (!input) {
        return null;
    }
    const digits = input.replace(/[^0-9]/g, "");
    if (!digits) {
        return null;
    }
    return `+${digits}`;
};

export interface PhoneValidationResult {
    isValid: boolean;
    errorCode?: PhoneErrorCode;
    message?: string;
    rawInput?: string;
    extension?: string | null;
    country?: string | null;
    countryCallingCode?: string | null;
    nationalNumber?: string | null;
    e164?: string | null;
    nationalFormat?: string | null;
    internationalFormat?: string | null;
    number?: string | null;
    formatNational?: string | null;
    formatInternational?: string | null;
    type?: string | null;
    isPossible?: boolean;
    lineType?: string | null;
    isSmsCapable?: boolean;
}

export interface PhoneValidationOptions {
    countryCode?: string;
    countryCallingCode?: string;
    allowSmsOnly?: boolean;
    countryFieldLabel?: string;
}

export enum PhoneErrorCode {
    PHONE_CONTAINS_INVALID_CHARS = "PHONE_CONTAINS_INVALID_CHARS",
    PHONE_INVALID_FORMAT = "PHONE_INVALID_FORMAT",
    PHONE_NOT_VALID_FOR_COUNTRY = "PHONE_NOT_VALID_FOR_COUNTRY",
    PHONE_NOT_SMS_CAPABLE = "PHONE_NOT_SMS_CAPABLE",
    PHONE_COUNTRY_REQUIRED = "PHONE_COUNTRY_REQUIRED",
}

const typeMap: Record<number, string> = {
    [PhoneNumberType.FIXED_LINE]: "FIXED_LINE",
    [PhoneNumberType.MOBILE]: "MOBILE",
    [PhoneNumberType.FIXED_LINE_OR_MOBILE]: "FIXED_LINE_OR_MOBILE",
    [PhoneNumberType.TOLL_FREE]: "TOLL_FREE",
    [PhoneNumberType.PREMIUM_RATE]: "PREMIUM_RATE",
    [PhoneNumberType.SHARED_COST]: "SHARED_COST",
    [PhoneNumberType.VOIP]: "VOIP",
    [PhoneNumberType.PERSONAL_NUMBER]: "PERSONAL_NUMBER",
    [PhoneNumberType.PAGER]: "PAGER",
    [PhoneNumberType.UAN]: "UAN",
    [PhoneNumberType.VOICEMAIL]: "VOICEMAIL",
    [PhoneNumberType.UNKNOWN]: "UNKNOWN",
};

let cachedRegionNameMap: Map<string, string> | null = null;

const normalizeCountryName = (name: string): string => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
};

const buildRegionNameMap = (): Map<string, string> => {
    if (cachedRegionNameMap) {
        return cachedRegionNameMap;
    }

    const map = new Map<string, string>();

    if (typeof Intl !== "undefined" && typeof (Intl as any).DisplayNames === "function") {
        const displayNames = new Intl.DisplayNames(["en"], { type: "region" });
        phoneUtil.getSupportedRegions().forEach((regionCode: string) => {
            const displayName = displayNames.of(regionCode);
            if (displayName) {
                map.set(normalizeCountryName(displayName), regionCode);
            }
        });
    }

    const aliases: Record<string, string> = {
        "united states": "US",
        "united states of america": "US",
        "united kingdom": "GB",
        "russia": "RU",
        "south korea": "KR",
        "north korea": "KP",
        "czechia": "CZ",
        "czech republic": "CZ",
        "eswatini": "SZ",
        "timor leste": "TL",
        "cabo verde": "CV",
        "palestine": "PS",
        "vatican city": "VA",
        "sao tome and principe": "ST",
        "sao tome and principe republic": "ST",
        "bolivia": "BO",
        "moldova": "MD",
        "tanzania": "TZ",
        "syria": "SY",
        "laos": "LA",
        "iran": "IR",
    };

    Object.entries(aliases).forEach(([name, code]) => {
        map.set(normalizeCountryName(name), code);
    });

    cachedRegionNameMap = map;
    return map;
};

export const getRegionCodeForCountryName = (countryName?: string | null): string | null => {
    if (!countryName) {
        return null;
    }

    const normalized = normalizeCountryName(countryName);
    if (!normalized) {
        return null;
    }

    const map = buildRegionNameMap();
    return map.get(normalized) ?? null;
};

export const validatePhoneNumber = (
    phoneNumber: string,
    options: PhoneValidationOptions = {}
): PhoneValidationResult => {
    const rawInput = phoneNumber;

    if (!phoneNumber || !phoneNumber.trim()) {
        return {
            isValid: false,
            errorCode: PhoneErrorCode.PHONE_INVALID_FORMAT,
            message: "Phone number is required",
        };
    }

    if (containsInvalidChars(phoneNumber)) {
        return {
            isValid: false,
            errorCode: PhoneErrorCode.PHONE_CONTAINS_INVALID_CHARS,
            message: "Phone number contains invalid characters",
        };
    }

    let normalized = normalizeInput(phoneNumber);
    const rawRegion = options.countryCode?.trim();
    const resolvedRegion = rawRegion
        ? (rawRegion.length === 2
            ? rawRegion.toUpperCase()
            : getRegionCodeForCountryName(rawRegion) ?? undefined)
        : undefined;

    if (!normalized.startsWith("+")) {
        const derivedCallingCode = normalizeCallingCode(options.countryCallingCode);
        if (derivedCallingCode) {
            const strippedLocal = normalized.replace(/^0+/, "");
            normalized = `${derivedCallingCode}${strippedLocal}`;
        } else if (!resolvedRegion) {
            return {
                isValid: false,
                errorCode: PhoneErrorCode.PHONE_COUNTRY_REQUIRED,
                message: "Country selection is required when phone number doesn't start with +",
            };
        }
    }

    try {
        const parsed = phoneUtil.parse(normalized, resolvedRegion);
        const isPossible = phoneUtil.isPossibleNumber(parsed);
        const isValid = phoneUtil.isValidNumber(parsed);

        if (!isPossible || !isValid) {
            return {
                isValid: false,
                errorCode: PhoneErrorCode.PHONE_NOT_VALID_FOR_COUNTRY,
                message: "Phone number is not valid for the selected country code",
            };
        }

        const numberType = phoneUtil.getNumberType(parsed);
        const lineType = typeMap[numberType] ?? "UNKNOWN";
        const isSmsCapable =
            numberType === PhoneNumberType.MOBILE ||
            numberType === PhoneNumberType.FIXED_LINE_OR_MOBILE ||
            numberType === PhoneNumberType.VOIP;

        if (options.allowSmsOnly && !isSmsCapable) {
            return {
                isValid: false,
                errorCode: PhoneErrorCode.PHONE_NOT_SMS_CAPABLE,
                message: "Phone number is not SMS capable",
                lineType,
            };
        }

        const e164 = phoneUtil.format(parsed, PhoneNumberFormat.E164);
        const nationalFormat = phoneUtil.format(parsed, PhoneNumberFormat.NATIONAL);
        const internationalFormat = phoneUtil.format(parsed, PhoneNumberFormat.INTERNATIONAL);

        return {
            isValid: true,
            rawInput,
            country: phoneUtil.getRegionCodeForNumber(parsed),
            countryCallingCode: `+${parsed.getCountryCode?.() ?? ""}`,
            nationalNumber: parsed.getNationalNumber()?.toString?.() ?? String(parsed.getNationalNumber()),
            e164,
            nationalFormat,
            internationalFormat,
            number: e164,
            formatNational: nationalFormat,
            formatInternational: internationalFormat,
            type: lineType,
            lineType,
            isPossible,
            isSmsCapable,
        };
    } catch (error) {
        return {
            isValid: false,
            errorCode: PhoneErrorCode.PHONE_INVALID_FORMAT,
            message: "Invalid phone number format",
        };
    }
};

export const isValidPhoneNumber = (phoneNumber: string, countryCode?: string): boolean => {
    try {
        const parsedNumber = phoneUtil.parseAndKeepRawInput(phoneNumber, countryCode || undefined);
        return parsedNumber ? phoneUtil.isValidNumber(parsedNumber) : false;
    } catch {
        return false;
    }
};

export const formatPhoneNumberE164 = (phoneNumber: string, countryCode?: string): string | null => {
    try {
        const parsedNumber = phoneUtil.parseAndKeepRawInput(phoneNumber, countryCode || undefined);
        if (parsedNumber && phoneUtil.isValidNumber(parsedNumber)) {
            return phoneUtil.format(parsedNumber, PhoneNumberFormat.E164);
        }
        return null;
    } catch {
        return null;
    }
};

export const formatPhoneNumberInternational = (phoneNumber: string, countryCode?: string): string | null => {
    try {
        const parsedNumber = phoneUtil.parseAndKeepRawInput(phoneNumber, countryCode || undefined);
        if (parsedNumber && phoneUtil.isValidNumber(parsedNumber)) {
            return phoneUtil.format(parsedNumber, PhoneNumberFormat.INTERNATIONAL);
        }
        return null;
    } catch {
        return null;
    }
};

export interface CountryInfo {
    code: string;
    callingCode: string;
}

export const getCountries = (): CountryInfo[] => {
    const regions = phoneUtil.getSupportedRegions();
    const countries: CountryInfo[] = [];

    regions.forEach((regionCode: string) => {
        const callingCode = phoneUtil.getCountryCodeForRegion(regionCode);
        if (callingCode) {
            countries.push({
                code: regionCode,
                callingCode: `+${callingCode}`,
            });
        }
    });

    return countries.sort((a, b) => a.code.localeCompare(b.code));
};

export const getCallingCodeForCountry = (countryCode: string): string | null => {
    try {
        const callingCode = phoneUtil.getCountryCodeForRegion(countryCode.toUpperCase());
        return callingCode ? `+${callingCode}` : null;
    } catch {
        return null;
    }
};
